import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Props = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  userId: string;
  onGranted?: () => void;
};

const CATEGORIES = [
  { value: "customer_service", label: "Customer service" },
  { value: "thank_you", label: "Thank you" },
  { value: "gift", label: "Gift" },
  { value: "promo", label: "Promotional" },
  { value: "other", label: "Other" },
];

export const GrantCreditsDialog = ({ open, onOpenChange, userId, onGranted }: Props) => {
  const [amount, setAmount] = useState<string>("100");
  const [category, setCategory] = useState<string>("thank_you");
  const [reason, setReason] = useState<string>("");

  const reset = () => {
    setAmount("100");
    setCategory("thank_you");
    setReason("");
  };

  const grant = useMutation({
    mutationFn: async () => {
      const amt = Number(amount);
      const { data, error } = await supabase.functions.invoke("admin-grant-credits", {
        body: {
          op: "grant",
          user_id: userId,
          amount: amt,
          category,
          reason: reason.trim(),
        },
      });
      if (error) throw new Error(error.message);
      const payload = data as { error?: string; balance_after?: number };
      if (payload?.error) throw new Error(payload.error);
      return payload;
    },
    onSuccess: (payload) => {
      toast.success(
        `Granted ${Number(amount).toLocaleString()} credits` +
          (payload?.balance_after != null
            ? ` · new balance ${payload.balance_after.toLocaleString()}`
            : ""),
      );
      onGranted?.();
      reset();
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message || "Could not grant credits"),
  });

  const amtNum = Number(amount);
  const valid =
    Number.isInteger(amtNum) &&
    amtNum >= 1 &&
    amtNum <= 10_000 &&
    reason.trim().length >= 10 &&
    reason.trim().length <= 500;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant AI credits</DialogTitle>
          <DialogDescription>
            Granted credits never expire and roll over with the user's other balance.
            Every grant is logged to the admin audit trail.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="grant-amount">Amount (credits)</Label>
            <Input
              id="grant-amount"
              type="number"
              min={1}
              max={10000}
              step={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Between 1 and 10,000 credits.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="grant-category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="grant-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="grant-reason">Reason (required)</Label>
            <Textarea
              id="grant-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Thank-you for early beta feedback on the publishing flow."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {reason.trim().length}/500 · minimum 10 characters
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={grant.isPending}>
            Cancel
          </Button>
          <Button disabled={!valid || grant.isPending} onClick={() => grant.mutate()}>
            {grant.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Grant credits"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
